<template>
  <div
    ref="mesure"
    class="accueil"
  >
    <!-- Le serveur réserve l'accueil par widgets au périmètre interne. Refusé,
         on sert les raccourcis seuls. -->
    <div
      v-if="interdit"
      class="accueil__repli"
    >
      <!-- Ce refus mérite d'être dit. `Home.vue` ne monte cet écran que pour
           quelqu'un que le serveur range du côté interne : arriver ici, c'est
           donc que le serveur l'a refusé quand même — un compte de catégorie
           `mixed`, un `mobiluce` seul. Sans un mot, il perdrait ses widgets sans
           jamais avoir de quoi le signaler au support. -->
      <el-alert
        class="accueil__avis"
        type="info"
        :closable="true"
        show-icon
        title="Votre accueil par widgets n'est pas disponible pour ce compte. Les accès rapides restent utilisables."
      />

      <WidgetRaccourcis />
    </div>

    <!-- L'ancienne grille de tuiles s'affichait sans réseau. Maintenant qu'elle
         est un widget, une panne du serveur emporterait aussi l'accès rapide :
         on la sert donc quand même, avec de quoi réessayer. -->
    <div
      v-else-if="panne"
      class="accueil__repli"
    >
      <p class="accueil__panne">
        Vos widgets n'ont pas pu être chargés.
        <el-button
          size="small"
          text
          @click="initialiser()"
        >
          Réessayer
        </el-button>
      </p>

      <WidgetRaccourcis />
    </div>

    <template v-else>
      <!-- `SectionHeader` n'expose qu'un slot par défaut, rendu à droite du
           titre : pas de slot nommé `actions`. -->
      <SectionHeader
        title="Mon accueil"
        icon="mdi:view-dashboard-outline"
      >
        <!-- Toujours visible, même quand tout est déjà posé : conditionner ce
             bouton à « il reste quelque chose à ajouter » effaçait la seule
             trace que cet écran est modulable, exactement quand l'utilisateur
             avait fini de le remplir. -->
        <el-button
          size="small"
          :type="bibliothequeOuverte ? 'primary' : 'default'"
          @click="bibliothequeOuverte = !bibliothequeOuverte"
        >
          <Icon
            :icon="bibliothequeOuverte ? 'mdi:check' : 'mdi:tune-variant'"
            :width="15"
          />
          <span style="margin-left: 5px;">
            {{ bibliothequeOuverte ? 'Terminer' : 'Personnaliser' }}
          </span>
          <el-tag
            v-if="!bibliothequeOuverte && aSignaler.length"
            size="small"
            type="primary"
            effect="dark"
            style="margin-left: 6px;"
          >
            {{ aSignaler.length }}
          </el-tag>
        </el-button>
      </SectionHeader>

      <!-- Un widget qui disparaît sans un mot passe pour un bug, pas pour un
           droit retiré. Le serveur n'en donne que le nombre : le nommer
           supposerait de publier ce à quoi cette personne n'a plus accès. -->
      <el-alert
        v-if="retires > 0"
        class="accueil__avis"
        type="info"
        :closable="true"
        show-icon
        :title="retires === 1
          ? '1 widget retiré de votre accueil : le droit correspondant vous a été supprimé.'
          : `${retires} widgets retirés de votre accueil : les droits correspondants vous ont été supprimés.`"
      />

      <!-- Un droit a changé pendant que l'écran était ouvert : le serveur a
           refusé une clé à l'enregistrement, l'écran s'est aligné sur lui. -->
      <el-alert
        v-if="aligneSurLeServeur"
        class="accueil__avis"
        type="warning"
        :closable="true"
        show-icon
        title="Vos droits ont changé depuis l'ouverture de cet écran : votre accueil a été remis à jour."
      />

      <!-- Panneau en place plutôt que fenêtre ou tiroir : `el-drawer` garde le
           focus prisonnier même sans voile, et une fenêtre téléportée perd le
           CSS scopé de ce fichier. -->
      <el-card
        v-if="bibliothequeOuverte"
        class="accueil__bibliotheque"
        shadow="never"
      >
        <p class="accueil__bibliotheque-titre">
          Les widgets de votre accueil. Cochez ceux que vous voulez voir ;
          déplacez-les ensuite par leur poignée.
        </p>

        <div class="accueil__offres">
          <button
            v-for="fiche in bibliotheque"
            :key="fiche.key"
            class="offre"
            :class="{ 'offre--posee': fiche.posee }"
            type="button"
            :aria-pressed="fiche.posee"
            @click="basculerWidget(fiche)"
          >
            <!-- Même pastille que sur la carte du widget : glyphe teinté sur
                 un fond de la même teinte très dilué. Un aplat plein ici et un
                 glyphe là, et c'est deux widgets différents pour l'œil. -->
            <span
              class="offre__pastille"
              :style="{
                background: `color-mix(in srgb, var(--teinte-${fiche.teinte}) 13%, transparent)`,
                color: `var(--teinte-${fiche.teinte})`,
              }"
              aria-hidden="true"
            >
              <Icon
                :icon="fiche.icone"
                :width="16"
              />
            </span>
            <span class="offre__marque">
              <Icon
                :icon="fiche.posee ? 'mdi:checkbox-marked' : 'mdi:checkbox-blank-outline'"
                :width="17"
              />
            </span>
            <span class="offre__texte">
              <span class="offre__titre">
                {{ fiche.titre }}
                <el-tag
                  v-if="aSignaler.includes(fiche.key)"
                  size="small"
                  type="primary"
                  effect="plain"
                >nouveau</el-tag>
              </span>
              <span class="offre__description">{{ fiche.description }}</span>
            </span>
          </button>
        </div>

        <!-- La disposition par défaut suit les droits et les tailles du
             moment : qui a réglé son accueil une fois ne profite plus jamais
             d'un réglage amélioré depuis, ni d'un widget mieux dimensionné.
             Ce bouton est la seule porte de sortie. -->
        <div class="accueil__pied">
          <el-popconfirm
            title="Revenir à la disposition par défaut ? Votre agencement sera perdu."
            confirm-button-text="Réinitialiser"
            cancel-button-text="Annuler"
            width="280"
            @confirm="remettreADefaut()"
          >
            <template #reference>
              <el-button
                size="small"
                text
                :loading="remiseEnCours"
              >
                Revenir à la disposition par défaut
              </el-button>
            </template>
          </el-popconfirm>
        </div>
      </el-card>

      <div
        v-if="chargement"
        v-loading="true"
        class="accueil__attente"
      />

      <template v-else>
        <p
          v-if="!surBureau"
          class="accueil__note"
        >
          Les widgets s'empilent sur cet écran. La disposition se règle sur un
          écran large.
        </p>

        <!-- Sur écran étroit, pas de grille du tout : une pile. La
             bibliothèque y réduit bien à une colonne, mais garde la hauteur en
             rangs réglée sur grand écran — or le même contenu y est deux à
             trois fois plus haut. Le bandeau et les accès rapides s'y
             coupaient. Une pile prend la hauteur de ce qu'elle contient, et
             rien ne s'y déplace de toute façon. -->
        <div
          v-if="!surBureau"
          class="pile"
        >
          <component
            :is="pose.composant"
            v-for="pose in pileOrdonnee"
            :key="pose.place.i"
            :donnees="etats[pose.place.i]?.donnees ?? null"
            :chargement="etats[pose.place.i]?.chargement ?? false"
            :panne="etats[pose.place.i]?.panne ?? false"
            :reglable="false"
            @recharger="charger(pose.place.i)"
          />
        </div>

        <!-- `:layout` en lecture seule, et non `v-model` : la bibliothèque émet
             `breakpoint-changed` PUIS, six lignes plus loin dans la même
             fonction, `update:layout` avec sa propre copie du palier. Un
             `v-model` laissait donc cette copie écraser le rétablissement fait
             juste avant, et le rétablissement ne servait à rien. -->
        <GridLayout
          v-else
          :layout="grille"
          :col-num="12"
          :row-height="30"
          :margin="[16, 16]"
          :is-draggable="surBureau"
          :is-resizable="surBureau"
          :is-bounded="true"
          :vertical-compact="true"
          :use-css-transforms="true"
          :responsive="true"
          @layout-updated="surDispositionMaj"
          @breakpoint-changed="surPalier"
          @update:layout="surLayoutRecalcule"
        >
          <GridItem
            v-for="pose in widgetsPoses"
            :key="pose.place.i"
            :i="pose.place.i"
            :x="pose.place.x"
            :y="pose.place.y"
            :w="pose.place.w"
            :h="pose.place.h"
            :min-w="3"
            :min-h="2"
            drag-allow-from=".widget__poignee"
            @moved="gesteFait"
            @resized="gesteFait"
          >
            <component
              :is="pose.composant"
              :donnees="etats[pose.place.i]?.donnees ?? null"
              :chargement="etats[pose.place.i]?.chargement ?? false"
              :panne="etats[pose.place.i]?.panne ?? false"
              :reglable="surBureau"
              @retirer="retirerWidget(pose.place.i)"
              @recharger="charger(pose.place.i)"
            />
          </GridItem>
        </GridLayout>

        <!-- Deux vides différents : celui qu'on peut remplir, et celui où il n'y
             a rien à proposer. Inviter à ajouter un widget quand aucun bouton ne
             le permet serait une impasse. -->
        <p
          v-if="!widgetsPoses.length && aProposer.length"
          class="accueil__vide"
        >
          Votre accueil est vide. Ajoutez un widget pour le remplir.
        </p>

        <p
          v-else-if="!widgetsPoses.length"
          class="accueil__vide"
        >
          Aucun widget ne vous est proposé pour le moment.
        </p>
      </template>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { GridItem, GridLayout } from 'grid-layout-plus'
import { Icon } from '@iconify/vue'
import { ElMessage } from 'element-plus'
import SectionHeader from '@/components/SectionHeader.vue'
import WidgetRaccourcis from '@/views/dashboard/widgets/WidgetRaccourcis.vue'
import { PALIER_BUREAU, useDashboardAccueil } from '@/composables/useDashboardAccueil'
import { composantDe } from '@/views/dashboard/widgets/registre'
import { apparenceDe } from '@/views/dashboard/widgets/apparence'

/**
 * L'accueil par widgets.
 *
 * Deux gardes tiennent la promesse « jamais d'écrasement d'une disposition
 * réglée » :
 *
 * 1. `layout-updated` ne suffit pas à déclencher un enregistrement. La
 *    bibliothèque l'émet aussi au montage et chaque fois que le nombre de
 *    widgets change — la première ouverture d'un compte enregistrerait donc le
 *    repli du serveur comme un choix de l'utilisateur, et celui-ci cesserait de
 *    suivre ses droits. Seuls `moved` et `resized`, que la bibliothèque n'émet
 *    qu'au relâchement d'un geste ayant réellement déplacé quelque chose, posent
 *    le drapeau.
 * 2. Rien ne part sous 1200 px. En dessous, la bibliothèque recalcule la
 *    disposition pour empiler les widgets sur moins de colonnes : c'est un
 *    affichage, pas un réglage. L'enregistrer reviendrait à imposer une colonne
 *    unique sur l'écran de bureau au prochain matin.
 */

const {
    chargement,
    interdit,
    panne,
    catalogue,
    grille,
    retires,
    aSignaler,
    aligneSurLeServeur,
    etats,
    palier,
    surBureau,
    widgetsPoses,
    aProposer,
    initialiser,
    reinitialiser,
    charger,
    retenir,
    ajouter,
    retirer,
    rendreLaDispositionDeBureau,
} = useDashboardAccueil()

const bibliothequeOuverte = ref(false)
const remiseEnCours = ref(false)

/**
 * Tous les widgets auxquels cette personne a droit, posés ou non.
 *
 * Le catalogue du serveur fait foi : `aProposer` n'en donne que le complément,
 * et s'en servir seul faisait disparaître la liste dès que tout était posé.
 */
const bibliotheque = computed(() => {
    const posees = new Set(grille.value.map(place => place.i))

    return catalogue.value
        .filter(fiche => composantDe(fiche.key) !== null)
        .map(fiche => ({ ...fiche, ...apparenceDe(fiche.key), posee: posees.has(fiche.key) }))
})

/** Pose le widget s'il ne l'est pas, le retire s'il l'est. */
/**
 * La pile, dans l'ordre où l'écran large les montre.
 *
 * L'ordre du tableau `grille` n'est pas celui de l'écran : un glisser ne
 * change que `x` et `y`, et le compactage de la bibliothèque réécrit ses
 * éléments à leur index d'origine. La grille s'en moquait — elle positionne en
 * absolu — mais une pile rend dans l'ordre du tableau. Sans ce tri, un widget
 * remonté en tête sur le poste restait en dernier sur le téléphone.
 */
const pileOrdonnee = computed(() =>
    [...widgetsPoses.value].sort((a, b) => a.place.y - b.place.y || a.place.x - b.place.x),
)

function basculerWidget(fiche) {
    return fiche.posee ? retirerWidget(fiche.key) : poserWidget(fiche.key)
}

/** Posé par `moved` / `resized`, consommé par le `layout-updated` qui suit. */
let gestePendant = false

/**
 * Le palier, quand la grille n'est pas là pour l'annoncer.
 *
 * Hors bureau, l'écran empile et ne monte pas la grille : c'est elle qui
 * émettait `breakpoint-changed`, donc plus rien n'annonçait le retour sur
 * écran large et l'accueil restait empilé jusqu'au rechargement.
 *
 * La mesure porte sur le conteneur et non sur la fenêtre, parce que c'est ce
 * que mesure `grid-layout-plus` : entre les deux il y a le menu latéral et les
 * marges de la page, soit près de 280 pixels menu ouvert. Une sonde sur la
 * fenêtre aurait annoncé le bureau à 1200 pixels là où la grille ne l'atteint
 * qu'à 1476 — l'écran aurait monté la grille puis l'aurait démontée deux
 * ticks plus tard, à chaque ouverture, sur tous les portables du parc. Et
 * replier le menu latéral, qui élargit le conteneur sans toucher à la fenêtre,
 * n'aurait rien déclenché.
 */
const LARGEUR_BUREAU = 1200

const mesure = ref(null)

let observateur = null

function surLargeur(largeur) {
    if (!largeur) return

    surPalier(largeur >= LARGEUR_BUREAU ? PALIER_BUREAU : 'md')
}

onMounted(() => {
    initialiser()

    if (typeof ResizeObserver !== 'function' || !mesure.value) return

    observateur = new ResizeObserver(entrees => surLargeur(entrees[0]?.contentRect?.width))
    observateur.observe(mesure.value)
})

onBeforeUnmount(() => {
    observateur?.disconnect()
})

function gesteFait() {
    gestePendant = true
}

async function surDispositionMaj(disposition) {
    if (!gestePendant) return

    gestePendant = false

    if (!surBureau.value) return

    const enregistre = await retenir(disposition)

    if (!enregistre) {
        ElMessage.warning("Votre disposition n'a pas pu être enregistrée.")
    }
}

/**
 * Le palier a changé.
 *
 * Au retour sur écran large, la disposition de bureau est réinstallée : la
 * bibliothèque garde une copie du palier prise à la première sortie et ne la
 * rafraîchit jamais, si bien qu'un aller-retour vers un petit écran ramènerait
 * une disposition antérieure aux derniers déplacements — et le geste suivant
 * l'enregistrerait, détruisant en base le réglage que l'utilisateur venait de
 * faire. Le drapeau de geste est remis à zéro au passage : un geste dont
 * l'enregistrement n'aurait pas eu lieu ne doit pas être imputé au palier
 * suivant.
 */
function surPalier(nouveau) {
    gestePendant = false
    palier.value = nouveau

    if (nouveau === PALIER_BUREAU) rendreLaDispositionDeBureau()
}

/**
 * La bibliothèque a recalculé la disposition pour un autre palier.
 *
 * Émis uniquement sur ce chemin-là, et toujours juste après
 * `breakpoint-changed` — donc `palier` est déjà à jour quand on arrive ici.
 *
 * De retour sur écran large, on l'ignore : ce que la bibliothèque propose alors
 * est sa copie du palier bureau, prise à la première sortie et jamais
 * rafraîchie. C'est `surPalier` qui vient de poser la bonne, et c'est elle qui
 * doit rester.
 */
function surLayoutRecalcule(recalcule) {
    if (surBureau.value) return

    grille.value = recalcule
}

async function poserWidget(cle) {
    const enregistre = await ajouter(cle)

    // `null` veut dire « rien à faire » — un double clic sur la même offre.
    // L'annoncer comme un échec affirmerait une panne qui n'a pas eu lieu.
    if (enregistre === false) {
        ElMessage.warning(
            "Le widget est affiché, mais votre disposition n'a pas pu être enregistrée : elle ne sera pas conservée au prochain chargement.",
        )
    }

}

/**
 * Retire un widget, et le dit si l'enregistrement échoue.
 *
 * Sans ce retour, le widget disparaissait de l'écran, l'utilisateur quittait la
 * page satisfait, et le retrouvait au chargement suivant sans avoir jamais su
 * que son retrait n'avait pas pris.
 */
async function retirerWidget(cle) {
    const enregistre = await retirer(cle)

    if (enregistre === false) {
        ElMessage.warning(
            "Le widget est retiré de l'écran, mais votre disposition n'a pas pu être enregistrée : il reviendra au prochain chargement.",
        )
    }
}

/**
 * Rend l'accueil à sa disposition par défaut.
 *
 * L'échec se dit : la disposition réglée est encore là, et un écran inchangé
 * après un clic de confirmation se lit comme un bouton mort.
 */
async function remettreADefaut() {
    remiseEnCours.value = true

    try {
        await reinitialiser()
        ElMessage.success('Votre accueil est revenu à sa disposition par défaut.')
    } catch {
        ElMessage.error("Votre accueil n'a pas pu être réinitialisé.")
    } finally {
        remiseEnCours.value = false
    }
}
</script>

<style lang="scss" scoped>
.accueil {
  &__avis {
    margin-bottom: 12px;
  }

  &__note {
    margin: 0 0 10px;
    font-size: 12px;
    color: var(--el-text-color-secondary);
  }

  &__attente {
    min-height: 180px;
  }

  &__panne {
    margin: 12px 0;
    font-size: 13px;
    color: var(--el-color-warning);
  }

  &__vide {
    margin: 12px 0;
    font-size: 13px;
    color: var(--el-text-color-secondary);
  }

  &__repli {
    margin-top: 8px;
  }

  &__bibliotheque {
    margin-bottom: 12px;
  }

  &__bibliotheque-titre {
    margin: 0 0 10px;
    font-size: 12.5px;
    color: var(--el-text-color-secondary);
  }

  /* La pile des écrans étroits : chaque widget prend la hauteur de son
     contenu, aucun ne se coupe. */
  &__offres {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 10px;
  }

  &__pied {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--el-border-color-lighter);
  }
}

.pile {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.offre {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 10px 12px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 12px;
  background: var(--el-bg-color);
  cursor: pointer;
  text-align: left;

  &:hover { border-color: var(--el-color-primary-light-5); }

  &:focus-visible {
    outline: 2px solid var(--el-color-primary);
    outline-offset: -2px;
  }

  &--posee {
    border-color: var(--el-color-primary-light-5);
    background: var(--el-color-primary-light-9);
  }

  &__pastille {
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    flex-shrink: 0;
  }

  &__marque {
    display: flex;
    order: 3;
    margin-left: auto;
    padding-top: 1px;
    color: var(--el-text-color-placeholder);
    flex-shrink: 0;
  }

  &--posee &__marque { color: var(--el-color-primary); }

  &__texte {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__titre {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--el-text-color-primary);
  }

  &__description {
    font-size: 11.5px;
    line-height: 1.35;
    color: var(--el-text-color-secondary);
  }
}

/* La grille : le placeholder d'Element Plus n'existe pas, celui de la
   bibliothèque est rouge par défaut. */
:deep(.vgl-layout) {
  --vgl-placeholder-bg: var(--el-color-primary);
  --vgl-placeholder-opacity: 12%;
  --vgl-resizer-border-color: var(--el-border-color-dark);

  margin: 0 -2px;
}

:deep(.vgl-item) {
  touch-action: auto;
}

:deep(.vgl-item--placeholder) {
  border-radius: 16px;
}
</style>

<!-- Les teintes des widgets et les deux couleurs d'état, définies une fois pour
     tous. Chaque teinte a deux valeurs :
     - l'accent, fond de la pastille d'icône : un glyphe blanc y garde au moins
       3:1, le seuil d'un élément graphique ;
     - l'encre, pour les titres et les chiffres : au moins 7:1 sur l'aplat du
       widget. Elle s'éclaircit en thème sombre, l'accent ne bouge pas. -->
<style>
:root {
  --teinte-indigo: #5b5bd6;
  --teinte-indigo-encre: #312e81;
  --teinte-corail: #e5484d;
  --teinte-corail-encre: #8a1c20;
  --teinte-emeraude: #059669;
  --teinte-emeraude-encre: #064e3b;
  --teinte-ambre: #d97706;
  --teinte-ambre-encre: #78350f;
  --teinte-violet: #7c3aed;
  --teinte-violet-encre: #4c1d95;
  --teinte-ciel: #0284c7;
  --teinte-ciel-encre: #0c4a6e;
  --teinte-rose: #db2777;
  --teinte-rose-encre: #831843;

  --accueil-attention: #c77700;
  --accueil-sain: #0f7b5f;

  /* La carte : une seule surface, neutre, pour les dix widgets. La teinte ne
     peint plus le fond — elle tient dans le glyphe et les accents de survol. */
  --accueil-carte: var(--el-bg-color);
  --accueil-bord: #e7e9ee;
  --accueil-bord-survol: #d5d9e2;
  --accueil-ombre:
    0 1px 2px rgba(16, 24, 40, 0.04),
    0 2px 6px rgba(16, 24, 40, 0.05);
  --accueil-ombre-survol:
    0 2px 4px rgba(16, 24, 40, 0.05),
    0 10px 24px rgba(16, 24, 40, 0.08);
}

html.dark {
  --teinte-indigo-encre: #c7d2fe;
  --teinte-corail-encre: #fecdca;
  --teinte-emeraude-encre: #a7f3d0;
  --teinte-ambre-encre: #fde68a;
  --teinte-violet-encre: #ddd6fe;
  --teinte-ciel-encre: #bae6fd;
  --teinte-rose-encre: #fbcfe8;

  --accueil-attention: #e8a33d;
  --accueil-sain: #35b08a;

  /* En thème sombre, c'est la carte qui s'éclaircit par rapport au fond : une
     ombre portée n'y est pas lisible, elle ne sert qu'à poser le relief. */
  --accueil-carte: #1c1e24;
  --accueil-bord: #2b2e36;
  --accueil-bord-survol: #3a3e48;
  --accueil-ombre: 0 1px 2px rgba(0, 0, 0, 0.3);
  --accueil-ombre-survol: 0 4px 16px rgba(0, 0, 0, 0.4);
}
</style>
